import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { getAllTokens, removeAllToken, removeOneToken } from "@/queries/tokens"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, ClockIcon, GlobeIcon, Trash } from "lucide-react"
import { useEffect, useState } from "react"
import DateObject from "react-date-object"
import persian from "react-date-object/calendars/persian"
import { getBrowserIcon, type Browsers } from "react-browser-icons"
import { toast } from "sonner"
export const Route = createFileRoute("/client/settings/security/")({
  component: SecuritySettings,
})

const BrowserIcon = ({ browser }: { browser: Browsers }) =>
  getBrowserIcon({ browser, className: "class", size: 22 })

function SecuritySettings() {
  const [allSessions, setAllSessions] = useState<
    Array<{ id: number; created_at: Date; ip: string; user_agent: string }>
  >([])
  const [activeSession, setActiveSession] = useState<{
    id: number
    created_at: Date
    ip: string
    user_agent: string
  }>()
  const [openDelete, setOpenDelete] = useState(false)
  const [selectedSession, setSelectedSession] = useState<{ id: number } | null>(
    null
  )
  const getClientTokens = useQuery({
    queryFn: () => getAllTokens(),
    queryKey: ["prizes", "client"],
  })

  const deleteOneToken = useMutation({
    mutationFn: (id: any) => removeOneToken(id),
    mutationKey: ["remove", "token"],
    onSuccess: (_, variables) => {
      toast.success("دسترسی با موفقیت حذف شد")
      setOpenDelete(false)
      setAllSessions((sessions) => sessions.filter((t) => t.id !== variables))
    },
  })

  const deleteAllToken = useMutation({
    mutationFn: () => removeAllToken(),
    mutationKey: ["remove", "all", "sessions"],
    onSuccess: () => {
      toast.success("تمامی دسترسی ها با موفقیت حذف شد")
      setOpenDelete(false)
      setAllSessions([])
    },
  })

  useEffect(() => {
    const tokens = getClientTokens.data?.data.data
    if (getClientTokens.data?.data.data) {
      const additionalId = getClientTokens.data.data.additional.id
      setAllSessions(tokens.filter((i: any) => i.id !== additionalId))
      setActiveSession(tokens.find((i: any) => i.id == additionalId))
    }
  }, [getClientTokens.data])

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Link to="/client/settings">
          <Button size="icon" variant="outline">
            <ArrowRight />
          </Button>
        </Link>

        <p className="text-xl font-bold no-font">تنظیمات امنیتی</p>
      </div>

      <Dialog open={openDelete} onOpenChange={setOpenDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedSession ? "حذف نشست" : "حذف تمامی نشست ها"}
            </DialogTitle>

            <p>
              {selectedSession
                ? "با حذف این نشست دیگر قابل دسترس نخواهد بود و خارج می شود"
                : "با حذف تمامی نشست تمام کاربران وارد شده به پنل خارج می شوند بجز حساب فعلی که وارد شده اید"}
            </p>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setOpenDelete(false)
                  setSelectedSession(null)
                }}
              >
                لغو
              </Button>
              <Button
                onClick={() => {
                  if (selectedSession) {
                    deleteOneToken.mutate(selectedSession.id)
                  } else {
                    deleteAllToken.mutate()
                  }
                }}
                isLoading={deleteOneToken.isPending || deleteAllToken.isPending}
              >
                حذف
              </Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2">
        <Card className="col-span-2 lg:col-span-1">
          <CardHeader>
            <CardTitle>نشت های فعال</CardTitle>
          </CardHeader>

          <CardContent>
            <div className="flex flex-col gap-2">
              <p>نشست فعلی</p>
              <Card>
                <CardContent className="flex flex-col gap-2 pt-4">
                  <div className="flex gap-2 items-center">
                    ip: {activeSession?.ip}
                    <GlobeIcon size={22} />
                  </div>
                  <div className="text-foreground/70 flex gap-2 items-center">
                    مرورگر: {activeSession?.user_agent}
                    <BrowserIcon
                      browser={activeSession?.user_agent as Browsers}
                    />
                  </div>
                  <div className="flex gap-2 items-center">
                    زمان ورود :
                    {new DateObject(new Date(activeSession?.created_at || ""))
                      .convert(persian)
                      .format("HH:mm YYYY/MM/DD ")}
                  </div>
                </CardContent>
              </Card>
              {allSessions.length < 1 ? (
                <>
                  <p className="text-center font-bold">
                    هیچ نشست فعال دیگری وجود ندارد
                  </p>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <p>نشست های دیگر : </p>

                    <Button
                      onClick={() => {
                        setOpenDelete(true)
                      }}
                    >
                      <Trash className="ml-2" />
                      حذف تمامی نشست ها
                    </Button>
                  </div>

                  {allSessions.map((session) => (
                    <Card>
                      <CardContent className="flex flex-col gap-2 pt-4">
                        <div className="flex gap-2 items-center">
                          ip: {session?.ip || "ثبت نشده است"}
                          <GlobeIcon size={22} />
                        </div>
                        <div className="text-foreground/70 flex gap-2 items-center">
                          مرورگر: {session?.user_agent || "ثبت نشده است"}
                          {session?.user_agent && (
                            <BrowserIcon
                              browser={session.user_agent as Browsers}
                            />
                          )}
                        </div>
                        <div className="flex gap-2 items-center">
                          زمان ورود :
                          {new DateObject(new Date(session?.created_at || ""))
                            .convert(persian)
                            .format("HH:mm YYYY/MM/DD ")}
                          <ClockIcon size={22} />
                        </div>

                        <Button
                          className="self-start"
                          onClick={() => {
                            setSelectedSession(session)
                            setOpenDelete(true)
                          }}
                        >
                          <Trash className="ml-2" />
                          حذف این نشست
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
